import React, { ChangeEventHandler, FocusEventHandler, ReactNode, useRef, useState } from "react";

import { Input as AntInput, InputRef } from "antd";
import { RuleObject } from "antd/es/form";

import { parseInputRules } from "utils";

import { Form, Icon, InputLabel, Popover } from "components";

interface InputPasswordProps {
  name: string;
  label: string;
  rules: RuleObject[];
  PasswordStrengthPopup: ReactNode;
  allowClear?: boolean;
  disabled?: boolean;
  initialValue?: string;
  message?: string;
  onBlur?: FocusEventHandler;
  onChange?: ChangeEventHandler;
  className?: string;
  dataTestId?: string;
}

export const InputPassword = ({
  allowClear,
  className = "",
  disabled,
  initialValue,
  name,
  label,
  message,
  rules,
  PasswordStrengthPopup,
  dataTestId,
  onBlur = () => {},
  onChange = () => {},
}: InputPasswordProps) => {
  const inputElement = useRef<InputRef>(null);
  const [hasBeenFocused, setHasBeenFocused] = useState(false);
  const [internalValue, setInternalValue] = useState(initialValue);

  const handleLabelClick = () => {
    setHasBeenFocused(true);
    inputElement.current?.focus();
  };

  const active = !!(hasBeenFocused || internalValue);
  const required = rules && rules[0] && rules[0].required;

  const PasswordInput = (
    <Form.Item
      className={`floating-input ${hasBeenFocused ? "active" : ""} ${disabled ? "disabled" : "message"} ${className}`}
      colon={false}
      label={<InputLabel active={active} required={required} label={label} handleClick={handleLabelClick} />}
      name={name}
      rules={parseInputRules(rules, message)}
      initialValue={initialValue}
    >
      <AntInput.Password
        allowClear={allowClear}
        disabled={disabled}
        ref={inputElement}
        data-testid={dataTestId}
        onChange={(e) => {
          setInternalValue(e.target.value);
          onChange(e);
        }}
        onFocus={() => setHasBeenFocused(true)}
        onBlur={(e) => {
          setHasBeenFocused(false);
          onBlur(e);
        }}
        iconRender={(visible) =>
          visible ? <Icon iconName="tabler/eye" style={{ cursor: "pointer" }} /> : <Icon iconName="tabler/eye-off" style={{ cursor: "pointer" }} />
        }
      />
    </Form.Item>
  );

  return (
    <>
      {PasswordStrengthPopup ? (
        <Popover content={PasswordStrengthPopup} trigger="focus" visible={!!PasswordStrengthPopup && hasBeenFocused}>
          {PasswordInput}
        </Popover>
      ) : (
        PasswordInput
      )}
    </>
  );
};
